import {
  getQueryWithParams,
  fetchQuery,
  postMutation,
} from "src/components/ApolloConnection/ApolloProviderComponent";
import {
  GET_NOTES,
  LIST_EYE_OBSERVATION_TEMPLATE,
  LIST_DIAGNOSIS_TEMPLATE,
  LIST_PROCEDURE_TEMPLATE,
  LIST_INSTRUCTION_TEMPLATE,
  LIST_PLANOFCARE_TEMPLATE,
  LIST_PROGNOSIS_TEMPLATE,
  LIST_NUTRIADV_TEMPLATE,
  LIST_IMMU_TEMPLATE,
  LIST_NOTES_TEMPLATE,
} from "src/components/graphql/GraphQLquery";
import {
  ADD_NOTE,
  ADD_NOTES_TEMPLATE,
} from "src/components/graphql/GraphQLmutation";
import { snackbar } from "@mobiscroll/react5";

export async function getNotes(data) {
  try {
    const response = await getQueryWithParams(GET_NOTES, data);
    
    if (!response || response.errors) {
      throw new Error(response?.errors?.[0]?.message || "Failed to fetch notes");
    }

    const result = response?.data?.getAppointment;
    
    // Check for error response (statusCode >= 400)
    if (result?.statusCode && result.statusCode >= 400) {
      throw new Error(result?.message || "Failed to fetch notes");
    }
    
    // Success case
    if (result?.data) {
      snackbar({
        message: result?.message || "Patient notes fetched successfully.",
        color: "success",
      });
      return { ...result.data };
    }
    throw new Error("No notes data returned");
  } catch (e) {
    snackbar({
      message: e.message,
      color: "danger",
    });
    console.error("Error at getNotes:", e);
    return false;
  }
}

export async function listEyeObservationTemplate() {
  try {
    const response = await getQueryWithParams(LIST_EYE_OBSERVATION_TEMPLATE);

    if (!response || response.errors) {
      throw new Error(response?.errors?.[0]?.message || "Failed to fetch eye observation templates");
    }

    const result = response?.data?.listEyeObservationTemplate;
    
    // Check for error response (statusCode >= 400)
    if (result?.statusCode && result.statusCode >= 400) {
      throw new Error(result?.message || "Failed to fetch eye observation templates");
    }
    
    // Success case
    if (result?.data && result.data.length > 0) {
      snackbar({
        message: result?.message || "Eye observation templates fetched successfully.",
        color: "success",
      });
      return result.data
        .map((item) => [item.column1, item.column2])
        .flatMap((num) => num);
    }
    return [];
  } catch (e) {
    snackbar({
      message: e.message,
      color: "danger",
    });
    console.error("Error at listEyeObservationTemplate:", e);
    return [];
  }
}

export async function addNote(data) {
  try {
    const response = await postMutation(ADD_NOTE, data);

    if (!response || response.errors) {
      throw new Error(response?.errors?.[0]?.message || "Failed to update notes");
    }

    const result = response?.data?.addNote;
    
    // Check for error response (statusCode >= 400)
    if (result?.statusCode && result.statusCode >= 400) {
      throw new Error(result?.message || "Failed to update notes");
    }
    
    // Success case
    if (result?.data) {
      snackbar({
        message: result?.message || "Patient notes updated successfully.",
        color: "success",
      });
      return result.data;
    }
    throw new Error("No note data returned");
  } catch (e) {
    snackbar({
      message: e.message,
      color: "danger",
    });
    console.error("Error at addNote:", e);
    return null;
  }
}

export async function listDiagnosisTemplates(search = "") {
  try {
    const response = await fetchQuery(LIST_DIAGNOSIS_TEMPLATE, { search });

    if (!response || response.errors) {
      throw new Error(response?.errors?.[0]?.message || "Failed to fetch diagnosis templates");
    }

    const result = response?.data?.listDiagnosisTemplates;
    
    // Check for error response (statusCode >= 400)
    if (result?.statusCode && result.statusCode >= 400) {
      throw new Error(result?.message || "Failed to fetch diagnosis templates");
    }
    
    // Success case - return data (no snackbar for template lists)
    if (result?.data) {
      return result.data.map((item) => item.diagnosisName);
    }
    return [];
  } catch (e) {
    snackbar({
      message: e.message,
      color: "danger",
    });
    console.error("Error at listDiagnosisTemplates:", e);
    return [];
  }
}

export async function listProcedureTableTemplates(search) {
  try {
    const response = await fetchQuery(LIST_PROCEDURE_TEMPLATE, { search });

    if (!response || response.errors) {
      throw new Error(response?.errors?.[0]?.message || "Failed to fetch procedure templates");
    }

    const result = response?.data?.listProcedureTableTemplates;
    
    // Check for error response (statusCode >= 400)
    if (result?.statusCode && result.statusCode >= 400) {
      throw new Error(result?.message || "Failed to fetch procedure templates");
    }
    
    // Success case
    if (result?.data) {
      return result.data.map((item) => item.procedureName);
    }
    return [];
  } catch (e) {
    snackbar({
      message: e.message,
      color: "danger",
    });
    console.error("Error at listProcedureTableTemplates:", e);
    return [];
  }
}

export async function listInstructionTemplates(search) {
  try {
    const response = await fetchQuery(LIST_INSTRUCTION_TEMPLATE, { search });

    if (!response || response.errors) {
      throw new Error(response?.errors?.[0]?.message || "Failed to fetch instruction templates");
    }

    const result = response?.data?.listInstructionTemplates;
    
    // Check for error response (statusCode >= 400)
    if (result?.statusCode && result.statusCode >= 400) {
      throw new Error(result?.message || "Failed to fetch instruction templates");
    }
    
    // Success case
    if (result?.data) {
      return result.data.map((item) => item.instruction);
    }
    return [];
  } catch (e) {
    snackbar({
      message: e.message,
      color: "danger",
    });
    console.error("Error at listInstructionTemplates:", e);
    return [];
  }
}

export async function listPlanOfCareTemplates(search) {
  try {
    const response = await fetchQuery(LIST_PLANOFCARE_TEMPLATE(search));

    if (!response || response.errors) {
      throw new Error(response?.errors?.[0]?.message || "Failed to fetch plan of care templates");
    }

    const result = response?.data?.listPlanOfCareTemplates;
    
    // Check for error response (statusCode >= 400)
    if (result?.statusCode && result.statusCode >= 400) {
      throw new Error(result?.message || "Failed to fetch plan of care templates");
    }
    
    // Success case
    if (result?.data) {
      return result.data.map((item) => item.planOfCare);
    }
    return [];
  } catch (e) {
    snackbar({
      message: e.message,
      color: "danger",
    });
    console.error("Error at listPlanOfCareTemplates:", e);
    return [];
  }
}

export async function listPrognosisTemplates(search) {
  try {
    const response = await fetchQuery(LIST_PROGNOSIS_TEMPLATE, { search });

    if (!response || response.errors) {
      throw new Error(response?.errors?.[0]?.message || "Failed to fetch prognosis templates");
    }

    const result = response?.data?.listPrognosisTemplates;
    
    // Check for error response (statusCode >= 400)
    if (result?.statusCode && result.statusCode >= 400) {
      throw new Error(result?.message || "Failed to fetch prognosis templates");
    }
    
    // Success case
    if (result?.data) {
      return result.data.map((item) => item.prognosis);
    }
    return [];
  } catch (e) {
    snackbar({
      message: e.message,
      color: "danger",
    });
    console.error("Error at listPrognosisTemplates:", e);
    return [];
  }
}

export async function listNutritionalAdviceTemplates(search) {
  try {
    const response = await fetchQuery(LIST_NUTRIADV_TEMPLATE, { search });

    if (!response || response.errors) {
      throw new Error(response?.errors?.[0]?.message || "Failed to fetch nutritional advice templates");
    }

    const result = response?.data?.listNutritionalAdviceTemplates;
    
    // Check for error response (statusCode >= 400)
    if (result?.statusCode && result.statusCode >= 400) {
      throw new Error(result?.message || "Failed to fetch nutritional advice templates");
    }
    
    // Success case
    if (result?.data) {
      return result.data.map((item) => item.nutritionalAdvice);
    }
    return [];
  } catch (e) {
    snackbar({
      message: e.message,
      color: "danger",
    });
    console.error("Error at listNutritionalAdviceTemplates:", e);
    return [];
  }
}

export async function listImmunizationStatusTemplates(search) {
  try {
    const response = await fetchQuery(LIST_IMMU_TEMPLATE, { search });

    if (!response || response.errors) {
      throw new Error(response?.errors?.[0]?.message || "Failed to fetch immunization status templates");
    }

    const result = response?.data?.listImmunizationStatusTemplates;
    
    // Check for error response (statusCode >= 400)
    if (result?.statusCode && result.statusCode >= 400) {
      throw new Error(result?.message || "Failed to fetch immunization status templates");
    }
    
    // Success case
    if (result?.data) {
      return result.data.map((item) => item.immunizationStatus);
    }
    return [];
  } catch (e) {
    snackbar({
      message: e.message,
      color: "danger",
    });
    console.error("Error at listImmunizationStatusTemplates:", e);
    return [];
  }
}

export async function addNotesTemplate(data) {
  try {
    const response = await postMutation(ADD_NOTES_TEMPLATE, data);
    
    if (!response || response.errors) {
      throw new Error(response?.errors?.[0]?.message || "Failed to add notes template");
    }

    const result = response?.data?.addDiagnosisNotesTemplate;
    
    // Check for error response (statusCode >= 400)
    if (result?.statusCode && result.statusCode >= 400) {
      throw new Error(result?.message || "Failed to add notes template");
    }
    
    // Success case
    snackbar({
      message: result?.message || "Notes template added successfully.",
      color: "success",
    });
    return true;
  } catch (e) {
    snackbar({
      message: e.message,
      color: "danger",
    });
    console.error("Error at addNotesTemplate:", e);
    return null;
  }
}

export async function listDiagnosisNotesTemplates(filters, search) {
  try {
    let response = await getQueryWithParams(
      LIST_NOTES_TEMPLATE,
      { search, filters },
      true
    );
    
    if (!response || response.errors) {
      throw new Error(response?.errors?.[0]?.message || "Failed to fetch diagnosis notes templates");
    }

    const result = response?.data?.listDiagnosisNotesTemplates;
    
    // Check for error response (statusCode >= 400)
    if (result?.statusCode && result.statusCode >= 400) {
      throw new Error(result?.message || "Failed to fetch diagnosis notes templates");
    }
    
    // Success case
    if (result?.data && result.data.length > 0) {
      snackbar({
        message: result?.message || "Diagnosis notes templates fetched successfully.",
        color: "success",
      });
    }
    return result?.data || [];
  } catch (e) {
    snackbar({
      message: e.message,
      color: "danger",
    });
    console.error("Error at listDiagnosisNotesTemplates:", e);
    return null;
  }
}
